/**
 * The workspace's "Changes N": how many changes others (and AI agents) made to the project since you last looked at
 * its Changes tab (the first time: the last 24 hours). Polled every minute while the page is visible; `seen` true (the
 * Changes tab is open) marks them seen, which brings the count to 0.
 */
import { useCallback, useEffect, useState } from 'react';
import { getChanges } from '../services/CollabService';
import { changesCountOf } from '../utils/workspaceModel';

const POLL_MS = 60000;
const DAY_MS = 24 * 3600 * 1000;
const seenKey = (projectId) => `flow.changes.seen.${projectId}`;

export function changesSeenAt(projectId, now = Date.now()) {
  try {
    const saved = localStorage.getItem(seenKey(projectId));
    if (saved && Number.isFinite(Date.parse(saved))) { return saved; }
  } catch { /* storage unavailable */ }
  return new Date(now - DAY_MS).toISOString();
}

export function markChangesSeen(projectId, now = Date.now()) {
  try { localStorage.setItem(seenKey(projectId), new Date(now).toISOString()); } catch { /* not remembered */ }
}

export default function useChangesCount(projectId, { seen = false, enabled = true } = {}) {
  const [count, setCount] = useState(0);

  const load = useCallback(async () => {
    if (!projectId) { return; }
    try {
      setCount(changesCountOf(await getChanges(projectId, { since: changesSeenAt(projectId), limit: 200 })));
    } catch {
      // keep the last count
    }
  }, [projectId]);

  useEffect(() => {
    setCount(0);
    if (!projectId || !enabled) { return undefined; }
    if (seen) {
      markChangesSeen(projectId);
      return () => markChangesSeen(projectId); // leaving the tab: what it showed counts as seen
    }
    load();
    const timer = setInterval(() => {
      if (typeof document === 'undefined' || document.visibilityState !== 'hidden') { load(); }
    }, POLL_MS);
    return () => clearInterval(timer);
  }, [projectId, seen, enabled, load]);

  return seen ? 0 : count;
}
